import { decodeHtmlEntitiesIteratively } from './decodeHtmlEntities';

export { decodeDisplayName } from './decodeDisplayName';

/**
 * Safe redirect URL validation for in-app navigation (CWE-601 open redirect prevention).
 * Only allows relative paths starting with '/'. Rejects absolute URLs and dangerous schemes.
 *
 * @param candidate - Raw value from URL params or sessionStorage (untrusted)
 * @param fallback - Path to return when candidate is invalid or unsafe
 * @returns A safe relative path for React Router navigate(), or fallback
 */
export function getSafeRedirectUrl(candidate: string | null | undefined, fallback: string): string {
  if (candidate == null || typeof candidate !== 'string') {
    return fallback;
  }

  const trimmed = decodeHtmlEntitiesIteratively(candidate.trim());
  if (trimmed === '') {
    return fallback;
  }

  // Do not decode: callers may pass already-decoded values (e.g. searchParams.get() decodes once).
  // Double decoding would corrupt query strings (e.g. %26 -> & becomes a separate param).
  // Reject newline/control chars in raw form to prevent injection.
  if (/[\r\n]/.test(trimmed) || /%0[aAdD]/.test(trimmed)) {
    return fallback;
  }

  const normalized = trimmed;
  // Must start with exactly one '/' (relative path)
  if (!normalized.startsWith('/')) {
    return fallback;
  }

  // Reject protocol-relative or absolute URLs: '//', 'http:', 'https:', 'javascript:', etc.
  if (normalized.startsWith('//')) {
    return fallback;
  }
  const afterSlash = normalized.slice(1);
  const colonIndex = afterSlash.indexOf(':');
  if (colonIndex !== -1) {
    const beforeColon = afterSlash.slice(0, colonIndex);
    if (!beforeColon.includes('/')) {
      return fallback;
    }
  }

  const lower = normalized.toLowerCase();
  const dangerous = ['javascript:', 'data:', 'vbscript:', 'file:'];
  for (const scheme of dangerous) {
    if (lower.startsWith('/' + scheme) || lower.startsWith(scheme)) {
      return fallback;
    }
  }

  return normalized;
}
